// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - QA prompt version review/rollback UI only
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * QA Prompts Settings
 *
 * System prompt templates used during voice pipeline QA/analysis (not the Voice Process upload UI).
 * List versions, view content, activate/rollback. RBAC: QA_PROMPTS_*.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import PageSectionHeader from '../common/PageSectionHeader';

const QAPromptsManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState({});
  const [loadingVersions, setLoadingVersions] = useState({});
  const [contentDialog, setContentDialog] = useState({ open: false, title: '', content: '' });
  const [activating, setActivating] = useState(null);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(API_ENDPOINTS.QA_PROMPTS + '/');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setPrompts(res.data.data);
      } else {
        setPrompts([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load QA prompts');
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadVersions = async (promptKey) => {
    setLoadingVersions((prev) => ({ ...prev, [promptKey]: true }));
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.QA_PROMPTS}/${encodeURIComponent(promptKey)}/versions`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setVersions((prev) => ({ ...prev, [promptKey]: res.data.data }));
      }
    } catch (err) {
      setVersions((prev) => ({ ...prev, [promptKey]: [] }));
    } finally {
      setLoadingVersions((prev) => ({ ...prev, [promptKey]: false }));
    }
  };

  const handleViewCurrent = async (promptKey, displayName) => {
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.QA_PROMPTS}/${encodeURIComponent(promptKey)}`);
      if (res.data?.success && res.data.data?.content != null) {
        setContentDialog({
          open: true,
          title: displayName || promptKey,
          content: res.data.data.content,
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load prompt content');
    }
  };

  const handleActivate = async (promptKey, versionId) => {
    setActivating(versionId);
    try {
      await apiClient.post(
        `${API_ENDPOINTS.QA_PROMPTS}/${encodeURIComponent(promptKey)}/versions/${versionId}/activate`
      );
      setVersions((prev) => ({ ...prev, [promptKey]: undefined }));
      loadVersions(promptKey);
      loadPrompts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate version');
    } finally {
      setActivating(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageSectionHeader
        icon={<ArticleIcon />}
        title="QA Prompts Settings"
        description="Configure versioned system prompts used in the voice analysis pipeline. This is separate from Voice Process (upload/files). Roll back to a previous version if needed."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {prompts.length === 0 ? (
        <Typography color="text.secondary">No QA prompts found.</Typography>
      ) : (
        prompts.map((p) => (
          <Card key={p.prompt_key} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {p.display_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewCurrent(p.prompt_key, p.display_name)}
                  >
                    View current
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => (versions[p.prompt_key] ? setVersions((prev) => ({ ...prev, [p.prompt_key]: undefined })) : loadVersions(p.prompt_key))}
                  >
                    {versions[p.prompt_key] ? 'Hide history' : 'Version history'}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Version {p.active_version_number} · {p.source} · {p.updated_at ? new Date(p.updated_at).toLocaleString() : '—'}
              </Typography>

              {loadingVersions[p.prompt_key] && (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {versions[p.prompt_key] && versions[p.prompt_key].length > 0 && (
                <Table size="small" sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions[p.prompt_key].map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>{v.version}</TableCell>
                        <TableCell>{v.source}</TableCell>
                        <TableCell>{v.created_at ? new Date(v.created_at).toLocaleString() : '—'}</TableCell>
                        <TableCell>{v.is_active ? <Chip label="Current" size="small" color="primary" /> : null}</TableCell>
                        <TableCell align="right">
                          {!v.is_active && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={activating === v.id}
                              onClick={() => handleActivate(p.prompt_key, v.id)}
                            >
                              {activating === v.id ? 'Activating...' : 'Activate'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={contentDialog.open} onClose={() => setContentDialog({ open: false, title: '', content: '' })} maxWidth="md" fullWidth>
        <DialogTitle>{contentDialog.title}</DialogTitle>
        <DialogContent>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {contentDialog.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentDialog({ open: false, title: '', content: '' })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QAPromptsManagement;
